import "./BotModuleTester.scss"
import React, { useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import {FormattedMessage, useIntl} from "react-intl";
import { SendHorizontal, RotateCcw, Info } from "lucide-react";
import {PostAssistant, PostAssistantWithId, GetAssistant, AddMessageToThread, CreateAndPollThread, CreateThread, GetThreadMessages} from "../../api/APIBot";
import {useSupportBotContext, useSupportBotData} from "./SupportBotData";
import {AutoResizeTextarea} from "../../UIElements";
import {useLanguageData} from "../../App";
import {processSingleBotInstructions, replaceInstructionVariables} from "../../utils/assistantUtils";

export default function BotModuleTester({ botData, updateRootData }) {
    const intl = useIntl();
    const {
        data: {
            assistantInstructionsConfig,
            supportBotInstructions,
            supportTopicInstructionBody,
            supportBotInstructionBody
        } = {},
    } = useSupportBotData();

    const extractRelevantConfig = (data) => {
        if (!data?.config) return null;
        return {
            generalInstructions: data.config.generalInstructions || "",
            topicData: data.config.topicData || {},
            supportTopics: data.config.supportTopics || [],
            materials: data.config.materials || [],
            supportRequestTarget: data.config.supportRequestTarget || "",
            name: data.config.name || "",
            version: data.config.version || ""
        };
    };

    const [messageContent, setMessageContent] = useState("");
    const [messages, setMessages] = useState([]);
    const [assistantId, setAssistantId] = useState(botData?.assistantid || null);
    const [threadId, setThreadId] = useState(null);
    const [isSending, setIsSending] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const toolEventHistoryRef = useRef({});
    const messagesEndRef = useRef(null);

    const [lastSyncedConfig, setLastSyncedConfig] = useState(() => extractRelevantConfig(botData));
    const [configChanged, setConfigChanged] = useState(false);

    const postAssistant = PostAssistant();
    const postAssistantWithId = PostAssistantWithId(assistantId);
    const getAssistant = GetAssistant(assistantId, !!assistantId);

    const createThread = CreateThread();
    const addMessageToThread = AddMessageToThread(threadId);
    const runAndPoll = CreateAndPollThread(threadId);
    const getThreadMessages = GetThreadMessages(threadId);

    const {instanceLang} = useLanguageData()
    const botLanguage = instanceLang || "en"

    // Sync assistantId with parent's botData.assistantid
    useEffect(() => {
        if (botData?.assistantid && botData.assistantid !== assistantId) {
            setAssistantId(botData.assistantid);
        }
    }, [botData?.assistantid]);

    // Detect configuration changes
    useEffect(() => {
        const currentConfig = extractRelevantConfig(botData);

        if (lastSyncedConfig && currentConfig) {
            const hasChanged = !_.isEqual(lastSyncedConfig, currentConfig);
            setConfigChanged(hasChanged);
        }
    }, [botData, lastSyncedConfig]);

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleReset = async () => {
        if (isSending) return;

        // Clear all state
        setMessages([]);
        setThreadId(null);
        toolEventHistoryRef.current = {};
        setMessageContent("");
        setConfigChanged(false);

        // Ensure assistant is ready for new conversation
        try {
            await ensureAssistantReady(true);
        } catch (err) {
            console.error("Failed to reset assistant:", err);
        }
    };

    const buildAssistantPostBody = useMemo(() => {
        if (!assistantInstructionsConfig?.postBody || !botData?.config) return null;

        const processed = replaceInstructionVariables(
            assistantInstructionsConfig,
            supportBotInstructions,
            supportTopicInstructionBody,
            supportBotInstructionBody,
            botLanguage
        );

        // Name: bot name + (id)
        processed.postBody.name = `${botData.config.name || "SupportBot"} (${botData.id})`;

        const botSection = processSingleBotInstructions({
            bot: botData,
            processedConfig: processed,
            intl,
            categoryData: null // botData already contains topicData
        });

        // Merge into main instructions
        processed.postBody.instructions = (processed.postBody.instructions || "")
            .replace(/{botInstructions}/g, botSection.trim())
            .replace(/{initialMessage}/g, ""); // Not used in tester

        return processed.postBody;
    }, [assistantInstructionsConfig, botData, intl, supportBotInstructions, supportTopicInstructionBody, supportBotInstructionBody, botLanguage]);

    const ensureAssistantReady = async (forceUpdate = false) => {
        if (!buildAssistantPostBody) throw new Error("Assistant config missing");

        // Update existing assistant
        if (assistantId) {
            // Skip update if no force update and config hasn't changed
            if (!forceUpdate && !configChanged) {
                return assistantId;
            }

            if (getAssistant.isLoading) {
                // wait by polling the query cache directly in a loop to keep this synchronous to our handler
                let attempts = 0;
                while (attempts < 30 && getAssistant.isLoading) {
                    // eslint-disable-next-line no-await-in-loop
                    await new Promise((r) => setTimeout(r, 200));
                    attempts += 1;
                }
            }

            if (getAssistant.isSuccess && getAssistant.data) {
                const postBody = _.cloneDeep(buildAssistantPostBody);
                if (getAssistant.data.tool_resources) postBody.tool_resources = getAssistant.data.tool_resources;
                if (getAssistant.data.tools) postBody.tools = getAssistant.data.tools;

                // update
                const res = await postAssistantWithId.mutateAsync(postBody);
                if (res?.data?.id) {
                    setAssistantId(res.data.id);
                    updateRootData("assistantid", res.data.id)
                    // Update last synced config after successful update
                    setLastSyncedConfig(extractRelevantConfig(botData));
                    setConfigChanged(false);
                }
                return res?.data?.id || assistantId;
            }

            if (getAssistant.isError) {
                // fall through to a create as safety
            }
        }

        // Create new
        const createRes = await postAssistant.mutateAsync(buildAssistantPostBody);
        const newId = createRes?.data?.id;
        if (!newId) throw new Error("Assistant creation failed");
        setAssistantId(newId);
        updateRootData("assistantid", newId)
        // Update last synced config after successful creation
        setLastSyncedConfig(extractRelevantConfig(botData));
        setConfigChanged(false);
        return newId;
    };

    const rebuildMessagesWithToolEvents = (apiMessages, currentToolEvents = null) => {
        const finalMessages = [];
        let userMessageIndex = 0;

        for (let i = 0; i < apiMessages.length; i++) {
            const msg = apiMessages[i];

            if (msg.role === 'user') {
                userMessageIndex++;
                finalMessages.push(msg);

                // Check if we have tool events for this user message
                const storedEvents = toolEventHistoryRef.current[userMessageIndex];
                const isLastUserMessage = i === apiMessages.map((m, idx) => m.role === 'user' ? idx : -1).filter(idx => idx >= 0).pop();

                // Add tool events after this user message
                if (isLastUserMessage && currentToolEvents && currentToolEvents.length > 0) {
                    // This is the current interaction, add new tool events
                    finalMessages.push(...currentToolEvents.map(e => ({
                        role: "system",
                        content: e
                    })));
                } else if (storedEvents) {
                    // Add previously stored tool events
                    finalMessages.push(...storedEvents.map(e => ({
                        role: "system",
                        content: e
                    })));
                }
            } else {
                finalMessages.push(msg);
            }
        }

        return finalMessages;
    };

    const textareaRef = useRef(null);

    const handleSendMessage = async (e) => {
        e?.preventDefault?.();
        if (!messageContent.trim() || isSending) return;

        setIsSending(true);
        setIsLoading(true);
        const userText = messageContent;
        setMessageContent("");
        textareaRef.current?.focus();

        // Add user message and loading indicator immediately
        const userMessage = { role: "user", content: userText };
        const loadingMessage = {
            role: "assistant",
            content: "",
            isLoading: true
        };

        setMessages((prev) => [...prev, userMessage, loadingMessage]);

        try {
            // Update assistant only if configuration has changed or doesn't exist yet
            const readyAssistantId = await ensureAssistantReady(false);

            // Thread
            let tId = threadId;
            if (!tId) {
                const t = await createThread.mutateAsync();
                tId = t?.id;
                if (!tId) throw new Error("Thread creation failed");
                setThreadId(tId);
            }

            // Add message to thread
            await addMessageToThread.mutateAsync({ role: "user", content: userText });

            // Run + Poll (also yields any tool call stubs to render)
            const { toolEvents } = await runAndPoll.mutateAsync({ assistantId: readyAssistantId });

            // Store tool events for this interaction
            if (toolEvents && toolEvents.length > 0) {
                // Count current user messages to determine position
                const latest = await getThreadMessages.refetch();
                const apiMessages = normalizeMessages(latest?.data?.data || []);
                const userMessageCount = apiMessages.filter(m => m.role === 'user').length;

                // Store tool events for this user message position
                toolEventHistoryRef.current[userMessageCount] = toolEvents;
            }

            // Fetch latest thread messages
            const latest = await getThreadMessages.refetch();
            const apiMessages = normalizeMessages(latest?.data?.data || []);

            // Rebuild messages with all tool events properly placed
            const finalMessages = rebuildMessagesWithToolEvents(apiMessages, toolEvents);

            setMessages(finalMessages);
        } catch (err) {
            console.error(err);

            // Remove the loading message and add error
            setMessages((prev) => {
                const withoutLoading = prev.filter(m => !m.isLoading);
                return [
                    ...withoutLoading,
                    {
                        role: "system",
                        content: intl.formatMessage({
                            defaultMessage: "Pahoittelut. Jotain meni pieleen.",
                            id: "botModuleTester.error",
                        }),
                    },
                ];
            });
        } finally {
            setIsSending(false);
            setIsLoading(false);
        }
    };

    return (
        <div className={"module-tester"}>
            <div className={"module-header"}>
                <h2>
                    {intl.formatMessage({
                        defaultMessage: "Test module",
                        id: "testBot",
                    })}
                    <button
                        className={"reset-button"}
                        onClick={handleReset}
                        disabled={isSending}
                        title={intl.formatMessage({
                            defaultMessage: "Reset conversation",
                            id: "resetConversation",
                        })}
                    >
                        <RotateCcw size={18} />
                    </button>
                </h2>
            </div>

            <div className={"messages"}>
                {messages.length > 0 ? (
                    <div className="messages-content">
                        {messages.map((m, i) => (
                            <div key={i} className={`msg msg--${m.role}${m.isLoading ? ' msg--loading' : ''}`}>
                                {
                                    m.isLoading ?
                                        <span className={"dot"}></span> :
                                        m.role === "system" ?
                                            <ToolEventComponent event={m.content} botData={botData} /> :
                                            m.content
                                }
                            </div>
                        ))}
                        <div ref={messagesEndRef} />
                    </div>
                ) : (
                    <div className={"messages-placeholder"}>
                        <p>
                            {intl.formatMessage({
                                defaultMessage: "Start testing by sending a message.",
                                id: "moduleTestingMessagesPlaceholder",
                            })}
                        </p>
                    </div>
                )}
            </div>

            {configChanged && messages.length > 0 && (
                <div className="config-change-notice">
                    <div className={"config-change-notice--container"}>
                        <Info size={18} />
                        <span>
                            {intl.formatMessage({
                                defaultMessage: "Module instructions have changed",
                                id: "botModuleTester.configChanged",
                            })}
                        </span>
                        <button
                            onClick={handleReset}
                            disabled={isSending}
                            className="reset-inline-btn"
                        >
                            {intl.formatMessage({
                                defaultMessage: "Empty conversation",
                                id: "emptyConversation",
                            })}
                        </button>
                    </div>
                </div>
            )}

            <div className={"input-container"}>
                <form onSubmit={handleSendMessage}>
                    <AutoResizeTextarea
                        ref={textareaRef}
                        rows={1}
                        maxRows={5}
                        value={messageContent}
                        onChange={(e) => setMessageContent(e.target.value)}
                        placeholder={intl.formatMessage({
                            defaultMessage: "Type message...",
                            id: "testModuleInputPlaceholder",
                        })}
                        submitOnEnter={true}
                        onSubmit={handleSendMessage}
                    />
                    <button type={"submit"} disabled={isSending || !buildAssistantPostBody}>
                        <SendHorizontal size={20} />
                    </button>
                </form>
            </div>
        </div>
    );
}

function ToolEventComponent({event, botData}) {
    const { data: { supportTopicCodes } } = useSupportBotContext();
    const getTopicTitle = (topicId) => {
        // First check our temporary storage
        if (botData.topicData && botData.topicData[topicId]?.title) {
            return botData.topicData[topicId].title;
        }

        return supportTopicCodes?.[topicId]?.title || topicId;
    };
    const intl = useIntl()
    if (event?.name === "create_supportneed") {
        const supportTopic = getTopicTitle(event?.args?.category)
        return <FormattedMessage
            id={"supportRequestCreated.moduleTester"}
            defaultMessage={'Annie created a support request {supportTopic}{br}with note: "{note}"'}
            values={{br: <br/>, supportTopic: <b>{supportTopic}</b>, note: <em>{event?.args?.description}</em>}}/>
    }
    return <>{intl.formatMessage({id: 'functionCalled.moduleTester', defaultMessage: 'Annie called a function.'})}</>
}

function normalizeMessages(apiMessages = []) {
    // Converts OpenAI Assistants v2 style messages into [{role, content}] flat list
    // Also keeps any earlier user/system messages already in UI coherent if caller merges.
    const simplified = apiMessages
        .slice()
        .sort((a, b) => a.created_at - b.created_at)
        .map((m) => {
            const role = m.role || "assistant";
            // Text first; if no text, stringify content for visibility
            let text = "";
            if (Array.isArray(m.content) && m.content.length > 0) {
                const block = m.content.find((c) => c.type === "text");
                if (block?.text?.value) text = block.text.value;
                else text = safeStringify(m.content);
            } else if (m.content?.[0]?.text?.value) {
                text = m.content[0].text.value;
            } else {
                text = safeStringify(m.content || m);
            }
            // Hide citations-looking tokens like 【...】
            text = text.replace(/【[^】]+】/g, "");
            return { role, content: text };
        });

    return simplified;
}

function safeStringify(obj) {
    try {
        return typeof obj === "string" ? obj : JSON.stringify(obj, null, 2);
    } catch {
        return String(obj ?? "");
    }
}